import axios from "axios";
import { errorActions } from "../helpers/auth_helper";
import {
  IAcceptMappingRequest,
  IAcceptMappingResponse,
  IDRFPaginated,
  IMatchCandidate,
  IMatchUnmappedResponse,
  IVendorInventoryDiffCategory,
  IVendorInventoryDiffResponse,
  IVendorInventoryHealthWindow,
  IVendorInventoryManualSyncResponse,
  IVendorInventoryRunFilters,
  IVendorInventorySource,
  IVendorInventorySourceFilters,
  IVendorInventorySourceHealth,
  IVendorInventorySyncRunDetail,
  IVendorInventorySyncRunSummary,
} from "../interfaces/vendor_inventory_dashboard.interface";

const BASE = `${process.env.REACT_APP_API_URL}/vendor-inventory`;

// ──────────────────────────── Sources ────────────────────────────────────

/**
 * GET /vendor-inventory/sources/  → paginated list of inventory sources.
 */
export async function getVendorInventorySources(
  filters: IVendorInventorySourceFilters = {}
): Promise<IDRFPaginated<IVendorInventorySource> | undefined> {
  try {
    const params: Record<string, string | number | boolean> = {
      page: filters.page ?? 1,
      page_size: filters.page_size ?? 25,
    };
    if (filters.vendor_id != null) params.vendor_id = filters.vendor_id;
    if (filters.is_enabled != null) params.is_enabled = filters.is_enabled;
    if (filters.source_type) params.source_type = filters.source_type;

    return await axios
      .get<IDRFPaginated<IVendorInventorySource>>(`${BASE}/sources/`, {
        params,
      })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error fetching inventory sources");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

/**
 * GET /vendor-inventory/sources/{id}/  → single source detail.
 */
export async function getVendorInventorySource(
  sourceId: number
): Promise<IVendorInventorySource | undefined> {
  try {
    return await axios
      .get<IVendorInventorySource>(`${BASE}/sources/${sourceId}/`)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error fetching inventory source");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

/**
 * POST /vendor-inventory/sources/{id}/sync/  → manually enqueue a sync task.
 * Returns 202 Accepted with the task id.
 */
export async function triggerVendorInventorySync(
  sourceId: number
): Promise<IVendorInventoryManualSyncResponse | undefined> {
  try {
    return await axios
      .post<IVendorInventoryManualSyncResponse>(
        `${BASE}/sources/${sourceId}/sync/`
      )
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error enqueuing manual sync");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

/**
 * GET /vendor-inventory/sources/{id}/health/?window=today|7d
 */
export async function getVendorInventorySourceHealth(
  sourceId: number,
  window: IVendorInventoryHealthWindow = "7d"
): Promise<IVendorInventorySourceHealth | undefined> {
  try {
    return await axios
      .get<IVendorInventorySourceHealth>(`${BASE}/sources/${sourceId}/health/`, {
        params: { window },
      })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error fetching source health");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

/**
 * GET /vendor-inventory/sources/{id}/runs/  → paginated run history.
 */
export async function getVendorInventorySourceRuns(
  sourceId: number,
  filters: IVendorInventoryRunFilters = {}
): Promise<IDRFPaginated<IVendorInventorySyncRunSummary> | undefined> {
  try {
    const params: Record<string, string | number> = {
      page: filters.page ?? 1,
      page_size: filters.page_size ?? 20,
    };
    return await axios
      .get<IDRFPaginated<IVendorInventorySyncRunSummary>>(
        `${BASE}/sources/${sourceId}/runs/`,
        { params }
      )
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error fetching source run history");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

// ──────────────────────────── Sync Runs ──────────────────────────────────

/**
 * GET /vendor-inventory/runs/{id}/  → run detail (no diff JSON).
 */
export async function getVendorInventoryRun(
  runId: number
): Promise<IVendorInventorySyncRunDetail | undefined> {
  try {
    return await axios
      .get<IVendorInventorySyncRunDetail>(`${BASE}/runs/${runId}/`)
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error fetching sync run");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

/**
 * GET /vendor-inventory/runs/{id}/diff/?category=...&page=...
 *   → paginated slice of one diff category.
 */
export async function getVendorInventoryRunDiff(
  runId: number,
  category: IVendorInventoryDiffCategory,
  page: number = 1,
  pageSize: number = 50
): Promise<IVendorInventoryDiffResponse | undefined> {
  try {
    return await axios
      .get<IVendorInventoryDiffResponse>(`${BASE}/runs/${runId}/diff/`, {
        params: { category, page, page_size: pageSize },
      })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error fetching run diff");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

// ──────────────────────── Unmapped SKU Matching ────────────────────────────

/**
 * GET /vendor-inventory/runs/{id}/match-unmapped/?top_n=3
 *   → fuzzy-match unmapped SKUs against internal Variants.
 */
export async function getUnmappedMatches(
  runId: number,
  topN: number = 3
): Promise<IMatchUnmappedResponse | undefined> {
  try {
    return await axios
      .get<IMatchUnmappedResponse>(`${BASE}/runs/${runId}/match-unmapped/`, {
        params: { top_n: topN },
      })
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error fetching unmapped matches");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}

/**
 * POST /vendor-inventory/runs/{id}/accept-mapping/
 *   → accept a fuzzy match and create/update VendorVariant.
 */
export async function acceptUnmappedMapping(
  runId: number,
  body: IAcceptMappingRequest
): Promise<IAcceptMappingResponse | undefined> {
  try {
    return await axios
      .post<IAcceptMappingResponse>(
        `${BASE}/runs/${runId}/accept-mapping/`,
        body
      )
      .then((res) => res?.data)
      .catch((error) => {
        console.error("accept-mapping error:", error?.response?.data || error);
        errorActions(error, "Error accepting mapping");
        return undefined;
      });
  } catch (error) {
    return undefined;
  }
}
